import { useContext, useEffect, useState } from "react";

import { TabPanels } from "@reach/tabs";
import styled from "styled-components/macro";
import { Auth } from "../../../components/Auth";
import { Tab } from "../../../components/ReachTabs/Tab";
import { TabList } from "../../../components/ReachTabs/TabList";
import { CustomTabPanel } from "../../../components/ReachTabs/TabPanel";
import type { DataMutate, Permission } from "../../../types/types";
import type {
  AttributeGroupSummary,
  CollectionSchema,
  PIMProduct,
  SupportedAssetAccessibility,
  TemplateTabData,
} from "../../../types/types.PIM";
import { AccessHomePage } from "./Access/AccessHomePage";
import { Activity } from "./Activity/Activity";
import { ProductAssets } from "./Assets/ProductAssetsTabs";
import { CollectionComponent } from "./Collections/CollectionComponent";
import type { IProductVisibilityData } from "./Connections/Connections";
import { ProductConnections } from "./Connections/Connections";
import { ProductGroupCard } from "./ProductOverview/ProductGroupCard";
import { SchemaTagCard } from "./ProductOverview/SchemaTagCard";
import { ProductPricingPortfolioTab } from "./ProductSkuListPrice/ProductPricingPortfolioTab";
import { ProductPricingTab } from "./ProductSkuListPrice/ProductPricingTab";
import { ProductTabPanel } from "./ProductTabPanel";
import { useStoreState } from "../../../util/util";

const ProductTab = styled(Tab).attrs({
  "data-event-type": "product_tab",
  "data-tab-type": "product_tab",
})``;

export const ProductTabs = ({
  product,
  hasPermission,
  t,
  replaceProductId,
  mutate,
  isPortfolio = false,
  roleIsGuest = false,
  roleIsBuyer = false,
  onVisibilityDataFetched,
  show_pricing_tab,
  isTabDisabled = false,
  isShowTableWithBulkAction = false,
  isShowGridAssetWithBulkAction = false,
  documentType,
}: {
  product: PIMProduct;
  hasPermission: (p?: Permission | Permission[] | undefined) => boolean;
  t: (s: string) => string;
  replaceProductId: (id: string) => void;
  mutate: DataMutate<PIMProduct>;
  isPortfolio?: boolean;
  roleIsGuest?: boolean;
  roleIsBuyer?: boolean;
  onVisibilityDataFetched?: (visibilityData: IProductVisibilityData) => void;
  show_pricing_tab: boolean;
  isTabDisabled?: boolean;
  isShowTableWithBulkAction?: boolean;
  isShowGridAssetWithBulkAction?: boolean;
  documentType?: string;
}) => {
  const { roleIsSomeKindOfSeller } = useContext(Auth);
  const { storefront_metadata } = useStoreState();
  const [tablists, set_tab_lists] = useState<JSX.Element[]>([]);
  const [tabpanels, set_tab_panels] = useState<JSX.Element[]>([]);

  useEffect(() => {
    // Access the enable_list_prices setting directly from storefront_metadata
    const enable_list_prices = storefront_metadata.enable_list_prices;

    // Determine if we should hide empty content (for portfolio or sellers without edit permissions)
    const shouldHideEmptyContent =
      isPortfolio ||
      (roleIsSomeKindOfSeller &&
        !(hasPermission("modify_products") && product.is_editable));

    const isSKUs = (collection: CollectionSchema) =>
      collection.name.toLowerCase().includes("skus");

    const skusCollection = product.product_schema.collections.find(isSKUs);
    const SEOGroup = product.product_schema.groups.find(
      (group) => group.name === "SEO"
    );

    const has_sku_values = product.product_skus?.some((sku) => !sku.is_sample);

    const can_view_list_prices =
      isPortfolio && roleIsGuest
        ? product.list_price_visibility === "available_everywhere"
        : roleIsBuyer
        ? product.list_price_visibility === "internal_and_customers" ||
          product.list_price_visibility === "available_everywhere"
        : true;

    const collectionHasValues = (collection: CollectionSchema) => {
      return (
        collection.value_rows &&
        collection.value_rows.length > 0 &&
        collection.value_rows.flatMap(({ values }) => values).length > 0
      );
    };

    const tabHasValues = (name: string) => {
      const tab = product.product_schema.layout.find(
        (tab) => tab.name === name
      );
      let showTab = false;
      tab?.items.forEach((item) => {
        if (item.type === "collection") {
          const collection = product.product_schema.collections.find(
            (collection) =>
              collection.name.toLowerCase() === item.name.toLowerCase()
          );
          if (
            collection?.is_visible &&
            (!shouldHideEmptyContent ||
              (collectionHasValues(collection) &&
                (!collection.is_restricted || roleIsSomeKindOfSeller)))
          ) {
            showTab = true;
          }
        } else {
          const group = product.product_schema.groups.find(
            (group) => group.name.toLowerCase() === item.name.toLowerCase()
          );

          const showGroup =
            (!!group?.attributes?.length &&
              group.name.toLowerCase() !== "seo" &&
              groupHasValues(group) &&
              ((!group.is_restricted && roleIsGuest) || !roleIsGuest)) ||
            !shouldHideEmptyContent;

          if (showGroup) {
            showTab = true;
          }
        }
      });

      return showTab;
    };

    const groupHasValues = (group: AttributeGroupSummary) => {
      let hasValues = false;
      const is_accessible = (accessibility?: SupportedAssetAccessibility) => {
        return (
          (roleIsGuest && accessibility === "public") ||
          accessibility !== "internal"
        );
      };
      group?.attributes?.forEach((attribute) => {
        if (
          attribute.values?.length > 0 &&
          ((!shouldHideEmptyContent && attribute.is_visible) ||
            (shouldHideEmptyContent &&
              roleIsGuest &&
              !attribute.is_restricted &&
              !attribute.is_not_applicable &&
              attribute.is_visible) ||
            (shouldHideEmptyContent &&
              !roleIsGuest &&
              attribute.is_visible &&
              !attribute.is_not_applicable) ||
            (shouldHideEmptyContent &&
              attribute.input_type === "image" &&
              is_accessible(attribute.values[0]?.visibility)))
        ) {
          hasValues = true;
        }
      });
      return hasValues;
    };

    const showSEOTab = (group: AttributeGroupSummary) => {
      return (
        !roleIsGuest &&
        !isPortfolio &&
        group?.attributes?.some((attribute) => attribute.is_visible)
      );
    };

    const NewTabsList: JSX.Element[] = [];
    const NewTabPanelList: JSX.Element[] = [];

    product.product_schema.layout?.forEach((tab: TemplateTabData) => {
      if (tab.is_enabled) {
        switch (tab.tab_type) {
          case "documents":
            if (
              hasPermission("view_assets") ||
              roleIsBuyer ||
              roleIsGuest ||
              roleIsSomeKindOfSeller
            ) {
              // sellers should see an empty table so that they can add to it. Otherwise
              // we are showing nothing for the public/buyer view.
              const roleIsNonSeller = roleIsGuest || roleIsBuyer;
              const hasAssetsOrExternalLinks =
                product.number_of_assets > 0 ||
                product.number_of_generated_assets > 0 ||
                product.asset_external_links.length > 0;
              // an external link is a type of asset, from the perspective of the UI.
              // Its a separate object for unknown reasons

              if (
                (roleIsNonSeller && hasAssetsOrExternalLinks) ||
                (hasPermission("view_assets") &&
                  !hasAssetsOrExternalLinks &&
                  !shouldHideEmptyContent) ||
                (hasPermission("view_assets") &&
                  hasAssetsOrExternalLinks &&
                  shouldHideEmptyContent) ||
                (roleIsSomeKindOfSeller && !shouldHideEmptyContent) ||
                (hasAssetsOrExternalLinks &&
                  shouldHideEmptyContent &&
                  roleIsSomeKindOfSeller)
              ) {
                NewTabsList.push(
                  <ProductTab
                    data-analytics-name={tab.name}
                    data-object-id={product.product_number ?? product.id}
                    key={tab.name}
                  >
                    {tab.display_name}
                  </ProductTab>
                );
                NewTabPanelList.push(
                  <CustomTabPanel key={tab.name} refreshKey={tab.name}>
                    <ProductAssets
                      product={product}
                      fetchProductData={mutate}
                      replaceProductId={replaceProductId}
                      viewMode={isPortfolio}
                      isShowTableWithBulkAction={isShowTableWithBulkAction}
                      isShowGridAssetWithBulkAction={
                        isShowGridAssetWithBulkAction
                      }
                      documentType={documentType}
                    />
                  </CustomTabPanel>
                );
              }
            }
            break;

          case "seo":
            if (
              SEOGroup &&
              (!shouldHideEmptyContent || groupHasValues(SEOGroup)) &&
              showSEOTab(SEOGroup)
            ) {
              NewTabsList.push(
                <ProductTab
                  data-analytics-name={tab.name}
                  data-object-id={product.product_number ?? product.id}
                  key={tab.name}
                >
                  {tab.display_name}
                </ProductTab>
              );
              NewTabPanelList.push(
                <CustomTabPanel key={tab.name} refreshKey={tab.name}>
                  <ProductGroupCard
                    product={product}
                    group={SEOGroup}
                    key={tab.name}
                    mutateProduct={mutate}
                    isPortfolio={false}
                    replaceProductId={replaceProductId}
                  />
                  <SchemaTagCard
                    template_id={product.product_schema.template_id}
                    product={product}
                  />
                </CustomTabPanel>
              );
            }
            break;

          case "sku":
            if (
              skusCollection &&
              roleIsSomeKindOfSeller &&
              skusCollection.is_visible &&
              (!shouldHideEmptyContent || collectionHasValues(skusCollection))
            ) {
              NewTabsList.push(
                <ProductTab
                  data-analytics-name={tab.name}
                  data-object-id={product.product_number ?? product.id}
                  key={tab.name}
                >
                  {tab.display_name}
                </ProductTab>
              );
              NewTabPanelList.push(
                <CustomTabPanel key={tab.name} refreshKey={tab.name}>
                  <CollectionComponent
                    collection={skusCollection}
                    product={product}
                    fetchProductData={mutate}
                    replaceProductId={replaceProductId}
                    isPortfolio={isPortfolio}
                  />
                </CustomTabPanel>
              );
            }
            break;

          default:
            if (tabHasValues(tab.name)) {
              NewTabsList.push(
                <ProductTab
                  data-analytics-name={tab.name}
                  data-object-id={product.product_number ?? product.id}
                  key={tab.name}
                >
                  {tab.display_name}
                </ProductTab>
              );
              NewTabPanelList.push(
                <CustomTabPanel key={tab.name} refreshKey={tab.name}>
                  <ProductTabPanel
                    tab={tab}
                    product={product}
                    mutateProduct={mutate}
                    replaceProductId={replaceProductId}
                    isPortfolio={isPortfolio}
                  />
                </CustomTabPanel>
              );
            }
            break;
        }
      }
    });

    if (!shouldHideEmptyContent) {
      if (hasPermission("modify_products")) {
        NewTabsList.push(
          <ProductTab
            data-analytics-name="Access"
            data-object-id={product.product_number ?? product.id}
            key="access"
          >
            {t("Access")}
          </ProductTab>
        );
        NewTabPanelList.push(
          <CustomTabPanel key="access" refreshKey="access">
            <AccessHomePage product={product} mutateProduct={mutate} />
          </CustomTabPanel>
        );
      }
      NewTabsList.push(
        <ProductTab
          data-analytics-name="Connections"
          data-object-id={product.product_number ?? product.id}
          key="connections"
        >
          {t("Connections")}
        </ProductTab>
      );
      NewTabPanelList.push(
        <CustomTabPanel key="connections" refreshKey="connections">
          <ProductConnections
            product={product}
            onVisibilityDataFetched={onVisibilityDataFetched}
          />
        </CustomTabPanel>
      );

      // Check enable_list_prices setting before showing List Prices tab
      if (
        has_sku_values &&
        enable_list_prices &&
        product.pim_product_type !== "master"
      ) {
        NewTabsList.push(
          <ProductTab
            data-analytics-name="Pricing"
            data-object-id={product.product_number ?? product.id}
            key="pricing"
          >
            {t("Pricing")}
          </ProductTab>
        );
        NewTabPanelList.push(
          <CustomTabPanel key="pricing" refreshKey="pricing">
            <ProductPricingTab
              product={product}
              mutate_product={mutate}
              replace_product_id={replaceProductId}
            />
          </CustomTabPanel>
        );
      }

      NewTabsList.push(
        <ProductTab
          data-analytics-name="Activity"
          data-object-id={product.product_number ?? product.id}
          key="activity"
        >
          {t("Activity")}
        </ProductTab>
      );
      NewTabPanelList.push(
        <CustomTabPanel key="activity" refreshKey="activity">
          <div>
            <Activity
              target_id={
                product.display_status === "staged" ||
                product.display_status === "unpublished_staged"
                  ? product.published_product_id
                  : product.id
              }
              mode="product"
            />
          </div>
        </CustomTabPanel>
      );
    } else {
      if (enable_list_prices && show_pricing_tab) {
        NewTabsList.push(
          <ProductTab
            data-analytics-name="Pricing"
            data-object-id={product.product_number ?? product.id}
            key="pricing"
          >
            {t("Pricing")}
          </ProductTab>
        );
        NewTabPanelList.push(
          <CustomTabPanel key="pricing" refreshKey="pricing">
            <ProductPricingPortfolioTab
              product={product}
              can_view_list_prices={can_view_list_prices && enable_list_prices}
            />
          </CustomTabPanel>
        );
      }
    }

    set_tab_lists(NewTabsList);
    set_tab_panels(NewTabPanelList);
  }, [
    hasPermission,
    show_pricing_tab,
    isPortfolio,
    isShowGridAssetWithBulkAction,
    isShowTableWithBulkAction,
    mutate,
    onVisibilityDataFetched,
    product,
    replaceProductId,
    roleIsBuyer,
    roleIsGuest,
    roleIsSomeKindOfSeller,
    storefront_metadata.enable_list_prices,
    t,
    documentType,
  ]);

  return (
    <>
      <TabList disabled={isTabDisabled}>{tablists}</TabList>
      <TabPanels>{tabpanels}</TabPanels>
    </>
  );
};
